import { defineMessages } from "../define";

/** Dashboard (admin and branch): stat cards, charts, branch map, branch quality scoring. */
export default defineMessages({
  en: {
    // dashboard pages
    "dashboard.overview": "Overview",
    "dashboard.scoring": "Scoring",
    "dashboard.orders": "Orders",
    "dashboard.branchComparisonHeading": "Branch performance comparison",
    "dashboard.accessDeniedTitle": "Access denied",
    "dashboard.accessDeniedDescription": "You do not have permission to view the dashboard.",
    // period names (lib/date-period-utils)
    "dashboard.period.thisWeek": "This week",
    "dashboard.period.thisMonth": "This month",
    "dashboard.period.pastDays.one": "Past {count} day",
    "dashboard.period.pastDays.few": "Past {count} days",
    "dashboard.period.pastDays.other": "Past {count} days",
    "dashboard.period.pastWeeks.one": "Past {count} week",
    "dashboard.period.pastWeeks.few": "Past {count} weeks",
    "dashboard.period.pastWeeks.other": "Past {count} weeks",
    "dashboard.period.pastMonths.one": "Past {count} month",
    "dashboard.period.pastMonths.few": "Past {count} months",
    "dashboard.period.pastMonths.other": "Past {count} months",
    // branch quality scoring table (Scoring tab)
    "dashboard.qualityScoring.title": "Branch quality scoring",
    "dashboard.qualityScoring.description":
      "Score = 100 − (penalty points / evaluated photos) × 10. Error columns show counts; each header shows the penalty per occurrence.",
    "dashboard.qualityScoring.photos": "Photos",
    "dashboard.qualityScoring.burnt": "Burnt (−{points} pts)",
    "dashboard.qualityScoring.undercooked": "Undercooked (−{points} pts)",
    "dashboard.qualityScoring.missingIngredients": "Missing / not ordered ingredients (−{points} pts each)",
    "dashboard.qualityScoring.notRemoved": "Not removed (−{points} pts each)",
    "dashboard.qualityScoring.missingPizzaSaver": "Missing pizza saver (−{points} pts)",
    "dashboard.qualityScoring.invalidPhoto": "Invalid photo (−{points} pts)",
    "dashboard.qualityScoring.noPizza": "No pizza (−{points} pts)",
    "dashboard.qualityScoring.loading": "Loading branch scores…",
    "dashboard.qualityScoring.noBranches": "No branches found.",
    // dashboard controls (branch filter)
    "controls.loadingBranches": "Loading branches…",
    "controls.allBranches": "All branches",
    "controls.searchBranch": "Search branches…",
    "controls.branches": "Branches",
    "controls.noBranches": "No branches",
    "controls.noBranchResults": "No branches found",
    "controls.all": "All",
    // stat cards
    "stats.totalScore": "Total score",
    "stats.nokNoRetake": "NOK not recaptured",
    "stats.evaluableImages": "Evaluable photos",
    "stats.vsPreviousPeriod": "vs. previous period",
    "stats.checkedPizzas": "Checked pizzas",
    "stats.nokPizzas": "NOK pizzas",
    "stats.penaltyPoints": "Penalty points",
    "stats.target": "Target: {value}",
    // photo capture quality card
    "captureQuality.title": "Photo capture quality",
    "captureQuality.loading": "Loading the photo breakdown…",
    "captureQuality.periodPhotos.one": "{count} photo in the selected period",
    "captureQuality.periodPhotos.few": "{count} photos in the selected period",
    "captureQuality.periodPhotos.other": "{count} photos in the selected period",
    "captureQuality.noPizza": "No pizza",
    "captureQuality.obstacle": "Obstacle or pizza not fully visible",
    "captureQuality.evaluable": "Evaluable photos",
    "captureQuality.category": "Category",
    "captureQuality.count": "Count",
    "captureQuality.share": "Share",
    // branch map
    "map.title": "Branch map",
    "map.description": "Active branches and their score in the selected period.",
    "map.loadingScores": "Loading scores…",
    "map.branchCount.one": "{count} branch on the map",
    "map.branchCount.few": "{count} branches on the map",
    "map.branchCount.other": "{count} branches on the map",
    "map.missingCoordinates.one": "{count} branch without coordinates",
    "map.missingCoordinates.few": "{count} branches without coordinates",
    "map.missingCoordinates.other": "{count} branches without coordinates",
    "map.markerPhotos.one": "{count} photo",
    "map.markerPhotos.few": "{count} photos",
    "map.markerPhotos.other": "{count} photos",
    // branch comparison chart
    "comparison.titleAll": "Performance overview for all branches",
    "comparison.titleOne": "Selected branch performance",
    "comparison.titleSelected": "Performance overview for selected branches",
    "comparison.descriptionAll":
      "Share of OK results per check for branches with data in the selected period. Shows up to 15 branches with the highest overall share.",
    "comparison.descriptionOne": "Share of OK results per check for the branch selected in the dashboard filter.",
    "comparison.descriptionSelected":
      "Share of OK results per check for {count} branches selected in the dashboard filter.",
    "comparison.defaultDescription": "Share of OK results per check ({period})",
    "comparison.noData": "No data for the selected branches and period.",
    "comparison.average": "Average",
    "comparison.overall": "Overall",
    "comparison.ingredients": "Ingredients",
    "comparison.extras": "Extras",
    "comparison.removals": "Removals",
    "comparison.pizzaSaver": "Pizza saver",
    // score by ingredient
    "componentsScore.title": "Score by ingredient",
    "componentsScore.bottom5": "Bottom 5",
    "componentsScore.top5": "Top 5",
    "componentsScore.noTrendData": "no data",
    "componentsScore.samples.one": "{count} sample",
    "componentsScore.samples.few": "{count} samples",
    "componentsScore.samples.other": "{count} samples",
    "componentsScore.errors.one": "{count} error",
    "componentsScore.errors.few": "{count} errors",
    "componentsScore.errors.other": "{count} errors",
    "componentsScore.points": "pp",
    "componentsScore.showErrors": "Show pizzas with this error",
    // quality score trend
    "trend.title.one": "Quality score trend ({count} day)",
    "trend.title.few": "Quality score trend ({count} days)",
    "trend.title.other": "Quality score trend ({count} days)",
    // regions of the Czech Republic (branch map)
    "region.centralBohemia": "Central Bohemian Region",
    "region.southBohemia": "South Bohemian Region",
    "region.plzen": "Plzeň Region",
    "region.karlovyVary": "Karlovy Vary Region",
    "region.liberec": "Liberec Region",
    "region.hradecKralove": "Hradec Králové Region",
    "region.pardubice": "Pardubice Region",
    "region.southMoravia": "South Moravian Region",
    "region.moraviaSilesia": "Moravian-Silesian Region",
    "region.praha": "Prague",
    "region.vysocina": "Vysočina Region",
    "region.olomouc": "Olomouc Region",
    "region.zlinsky": "Zlín Region",
    "region.ustecky": "Ústí nad Labem Region",
  },
  cs: {
    // dashboard pages
    "dashboard.overview": "Přehled",
    "dashboard.scoring": "Bodování",
    "dashboard.orders": "Objednávky",
    "dashboard.branchComparisonHeading": "Srovnání výkonnosti poboček",
    "dashboard.accessDeniedTitle": "Přístup odepřen",
    "dashboard.accessDeniedDescription": "Nemáte oprávnění k zobrazení dashboardu.",
    // period names (lib/date-period-utils)
    "dashboard.period.thisWeek": "Tento týden",
    "dashboard.period.thisMonth": "Tento měsíc",
    "dashboard.period.pastDays.one": "Poslední {count} den",
    "dashboard.period.pastDays.few": "Poslední {count} dny",
    "dashboard.period.pastDays.other": "Posledních {count} dní",
    "dashboard.period.pastWeeks.one": "Poslední {count} týden",
    "dashboard.period.pastWeeks.few": "Poslední {count} týdny",
    "dashboard.period.pastWeeks.other": "Posledních {count} týdnů",
    "dashboard.period.pastMonths.one": "Poslední {count} měsíc",
    "dashboard.period.pastMonths.few": "Poslední {count} měsíce",
    "dashboard.period.pastMonths.other": "Posledních {count} měsíců",
    // branch quality scoring table (Scoring tab)
    "dashboard.qualityScoring.title": "Bodování kvality poboček",
    "dashboard.qualityScoring.description":
      "Skóre = 100 − (trestné body / vyhodnocené fotky) × 10. Sloupce chyb ukazují počty, záhlaví sloupce uvádí srážku za každý výskyt.",
    "dashboard.qualityScoring.photos": "Fotky",
    "dashboard.qualityScoring.burnt": "Spálené (−{points} b)",
    "dashboard.qualityScoring.undercooked": "Nedopečené (−{points} b)",
    "dashboard.qualityScoring.missingIngredients": "Chybějící / navíc suroviny (−{points} b/ks)",
    "dashboard.qualityScoring.notRemoved": "Neodebrané suroviny (−{points} b/ks)",
    "dashboard.qualityScoring.missingPizzaSaver": "Chybějící pizza saver (−{points} b)",
    "dashboard.qualityScoring.invalidPhoto": "Neplatná fotka (−{points} b)",
    "dashboard.qualityScoring.noPizza": "Bez pizzy (−{points} b)",
    "dashboard.qualityScoring.loading": "Načítání skóre poboček…",
    "dashboard.qualityScoring.noBranches": "Nebyly nalezeny žádné pobočky.",
    // dashboard controls (branch filter)
    "controls.loadingBranches": "Načítání poboček…",
    "controls.allBranches": "Všechny pobočky",
    "controls.searchBranch": "Hledat pobočku…",
    "controls.branches": "Pobočky",
    "controls.noBranches": "Žádné pobočky",
    "controls.noBranchResults": "Žádná pobočka nenalezena",
    "controls.all": "Všechny",
    // stat cards
    "stats.totalScore": "Celkové skóre",
    "stats.nokNoRetake": "NEOK bez nového pořízení",
    "stats.evaluableImages": "Hodnotitelné fotky",
    "stats.vsPreviousPeriod": "oproti předchozímu období",
    "stats.checkedPizzas": "Zkontrolované pizzy",
    "stats.nokPizzas": "NEOK pizzy",
    "stats.penaltyPoints": "Trestné body",
    "stats.target": "Cíl: {value}",
    // photo capture quality card
    "captureQuality.title": "Kvalita pořízených fotek",
    "captureQuality.loading": "Načítání rozdělení fotek…",
    "captureQuality.periodPhotos.one": "{count} fotka ve vybraném období",
    "captureQuality.periodPhotos.few": "{count} fotky ve vybraném období",
    "captureQuality.periodPhotos.other": "{count} fotek ve vybraném období",
    "captureQuality.noPizza": "Bez pizzy",
    "captureQuality.obstacle": "Překážka nebo pizza není celá vidět",
    "captureQuality.evaluable": "Hodnotitelné fotky",
    "captureQuality.category": "Kategorie",
    "captureQuality.count": "Počet",
    "captureQuality.share": "Podíl",
    // branch map
    "map.title": "Mapa poboček",
    "map.description": "Aktivní pobočky a jejich skóre ve vybraném období.",
    "map.loadingScores": "Načítání skóre…",
    "map.branchCount.one": "{count} pobočka na mapě",
    "map.branchCount.few": "{count} pobočky na mapě",
    "map.branchCount.other": "{count} poboček na mapě",
    "map.missingCoordinates.one": "{count} pobočka bez souřadnic",
    "map.missingCoordinates.few": "{count} pobočky bez souřadnic",
    "map.missingCoordinates.other": "{count} poboček bez souřadnic",
    "map.markerPhotos.one": "{count} fotka",
    "map.markerPhotos.few": "{count} fotky",
    "map.markerPhotos.other": "{count} fotek",
    // branch comparison chart
    "comparison.titleAll": "Přehled výkonnosti všech poboček",
    "comparison.titleOne": "Výkonnost vybrané pobočky",
    "comparison.titleSelected": "Přehled výkonnosti vybraných poboček",
    "comparison.descriptionAll":
      "Podíl výsledků OK podle kontroly u poboček s daty ve vybraném období. Zobrazeno je až 15 poboček s nejvyšším celkovým podílem.",
    "comparison.descriptionOne": "Podíl výsledků OK podle kontroly u pobočky vybrané ve filtru dashboardu.",
    "comparison.descriptionSelected":
      "Podíl výsledků OK podle kontroly u {count} poboček vybraných ve filtru dashboardu.",
    "comparison.defaultDescription": "Podíl výsledků OK podle kontroly ({period})",
    "comparison.noData": "Pro vybrané pobočky a období nejsou žádná data.",
    "comparison.average": "Průměr",
    "comparison.overall": "Celkově",
    "comparison.ingredients": "Suroviny",
    "comparison.extras": "Přídavky",
    "comparison.removals": "Odebrané suroviny",
    "comparison.pizzaSaver": "Pizza saver",
    // score by ingredient
    "componentsScore.title": "Skóre podle surovin",
    "componentsScore.bottom5": "Nejhorších 5",
    "componentsScore.top5": "Nejlepších 5",
    "componentsScore.noTrendData": "bez dat",
    "componentsScore.samples.one": "{count} vzorek",
    "componentsScore.samples.few": "{count} vzorky",
    "componentsScore.samples.other": "{count} vzorků",
    "componentsScore.errors.one": "{count} chyba",
    "componentsScore.errors.few": "{count} chyby",
    "componentsScore.errors.other": "{count} chyb",
    "componentsScore.points": "p. b.",
    "componentsScore.showErrors": "Zobrazit pizzy s touto chybou",
    // quality score trend
    "trend.title.one": "Trend skóre kvality ({count} den)",
    "trend.title.few": "Trend skóre kvality ({count} dny)",
    "trend.title.other": "Trend skóre kvality ({count} dní)",
    // regions of the Czech Republic (branch map)
    "region.centralBohemia": "Středočeský kraj",
    "region.southBohemia": "Jihočeský kraj",
    "region.plzen": "Plzeňský kraj",
    "region.karlovyVary": "Karlovarský kraj",
    "region.liberec": "Liberecký kraj",
    "region.hradecKralove": "Královéhradecký kraj",
    "region.pardubice": "Pardubický kraj",
    "region.southMoravia": "Jihomoravský kraj",
    "region.moraviaSilesia": "Moravskoslezský kraj",
    "region.praha": "Praha",
    "region.vysocina": "Kraj Vysočina",
    "region.olomouc": "Olomoucký kraj",
    "region.zlinsky": "Zlínský kraj",
    "region.ustecky": "Ústecký kraj",
  },
});
